import {
  Box,
  Collapse,
  Divider,
  Stack,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useAppSelecter } from "../../hooks/storeHooks";
import { RootState } from "../../store/store";
import styled from "@emotion/styled";
import ClusterList from "./ClusterList";
type ReviewBadgeProps = {
  reviewed: number;
  total: number;
  pending: number;
  save_for_later: number;
};
export const StyledTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#fff",
    color: "#333",
    boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    padding: "10px 12px",
    maxWidth: "fit-content",
  },
}));
export function ReviewBadge({
  reviewed,
  total,
  pending,
  save_for_later,
}: ReviewBadgeProps) {
  if (total === 0) return null;

  const pendingCount = pending + save_for_later;
  const laterCount = save_for_later ?? 0;

  let color: "default" | "success" | "warning" = "default";

  if (pendingCount === total) color = "default"; // all reviewed
  else if (pendingCount === 0) color = "success"; // all pending
  else color = "warning"; // partially reviewed

  return (
    <StyledTooltip
      arrow
      placement="top"
      title={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {" "}
          <Chip
            label={`Pending: ${pendingCount}`}
            color="warning"
            size="small"
          />
          <Chip
            label={`Mark for later: ${laterCount}`}
            color="warning"
            size="small"
            variant="outlined"
            sx={{ m: "8px 0" }}
          />
          <Chip
            label={`Reviewed: ${reviewed}`}
            color="success"
            size="small"
            sx={{ margin: "0 0 8px 0" }}
          />
          <Chip
            label={`Total: ${total}`}
            color={"default"}
            variant={"outlined"}
            size="small"
          />
        </Box>
      }
    >
      <Chip
        size="small"
        label={`${pendingCount}/${total}`}
        color={color}
        variant={color === "default" ? "outlined" : "filled"}
        sx={{ fontSize: "0.7rem", height: 22, cursor: "pointer" }}
      />
    </StyledTooltip>
  );
}
export const colors = ["#E57373", "#64B5F6", "#81C784", "#FFD54F"]; // add as many as needed

function SubtopicCard({ setFilters, filters, localFilterState }: any) {
  const [open, setOpen] = useState<string | null>(filters.subtopic_id ?? null);
  const { masters } = useAppSelecter(
    (state: RootState) => state.contentFeedback
  );
  const toggleSubtopic = (name: string, id: string) => {
    setFilters((prev: any) =>
      open === id
        ? {
            ...prev,
            subtopic: "",
            subtopic_id: "",
            cluster_id: "",
            cluster: "",
            cluster_color: "",
          }
        : {
            ...prev,
            subtopic: open === id ? "" : name,
            subtopic_id: open === id ? "" : id,
          }
    );
    setOpen((prev) => (open === id ? null : id));
  };

  const { subtopics, clusters } = masters;

  return (
    <Stack
      boxShadow="0 2px 6px rgba(0,0,0,0.1)"
      sx={{
        borderRadius: "8px",
        bgcolor: "white",
        mt: "8px",
        flex: 8,
        overflow: "hidden",
      }}
      direction="column"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: "10px 16px", bgcolor: "#c6c6c666" }}
      >
        <Typography variant="body1" fontWeight="bold">
          List of All Subtopics in this topic
        </Typography>{" "}
        <Typography variant="subtitle2" fontWeight="bold" color="primary">
          {
            subtopics.filter((s) =>
              localFilterState.subtopic !== "all"
                ? localFilterState.subtopic === "pending"
                  ? s.pending + s.save_for_later !== 0
                  : s.pending + s.reviewed + s.save_for_later !== 0
                : true
            ).length
          }
        </Typography>
      </Stack>

      <Divider />

      <Box
        sx={{ flex: 8, overflowY: "auto" }}
        className="scrollBluePrimarythin"
      >
        {subtopics
          .filter((s) =>
            localFilterState.subtopic !== "all"
              ? localFilterState.subtopic === "pending"
                ? s.pending + s.save_for_later !== 0
                : s.pending + s.reviewed + s.save_for_later !== 0
              : true
          )
          .map(({ pending, reviewed, name, id, save_for_later }) => {
            // calculate aggregated pending for badge
            const total = pending + reviewed + save_for_later;
            const isDisable = save_for_later + reviewed + pending === 0;
            return (
              <Box
                key={id}
                sx={
                  subtopics.filter((s) =>
                    localFilterState.subtopic !== "all"
                      ? localFilterState.subtopic === "pending"
                        ? s.pending + s.save_for_later !== 0
                        : s.pending + s.reviewed + s.save_for_later !== 0
                      : true
                  ).length === 1
                    ? { p: "8px 16px", flex: 8 }
                    : { p: "8px 16px" }
                }
              >
                {/* Subtopic Header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={
                    isDisable ? () => {} : () => toggleSubtopic(name, id)
                  }
                  sx={
                    isDisable
                      ? {
                          borderRadius: "6px",
                          p: "6px 8px",
                        }
                      : {
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#f9f9f9" },
                          borderRadius: "6px",
                          p: "6px 8px",
                        }
                  }
                >
                  <Typography
                    fontWeight="500"
                    color={isDisable ? "#bdbdbd" : "auto"}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 8,
                    }}
                  >
                    {name}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ReviewBadge
                      reviewed={reviewed}
                      total={total}
                      save_for_later={save_for_later}
                      pending={pending}
                    />
                    {isDisable ? (
                      ""
                    ) : open === id ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </Stack>
                </Stack>

                {/* Subtopic Content */}
                <Collapse
                  in={open === id}
                  timeout="auto"
                  unmountOnExit
                  className="scrollBluePrimarythin"
                  sx={clusters
                        .filter(
                          (s) => s.pending + s.reviewed + s.save_for_later !== 0
                        )
                        .filter((s) =>
                          localFilterState.cluster !== "all"
                            ? localFilterState.cluster === "pending"
                              ? s.pending + s.save_for_later !== 0
                              : s.pending + s.reviewed + s.save_for_later !== 0
                            : true
                        ).length > 24
                        ?{
                    maxHeight: "calc(100vh -  362.53px)"}:{
                    overflowY: "auto",
                    maxHeight: "378px",
                  }}
                >
                  <ClusterList
                    clusters={clusters}
                    open={open}
                    id={id}
                    filters={filters}
                    setFilters={setFilters}
                    localFilterState={localFilterState}
                  />
                </Collapse>
                <Divider sx={{ mt: 1 }} />
              </Box>
            );
          })}
      </Box>
    </Stack>
  );
}

export default SubtopicCard;
